import { Fragment } from "react";
import {
  MoveRight,
  Check,
  FileText,
  Folder,
  Send,
  Upload,
  PhoneCall,
  UserRound,
} from "lucide-react";

const CHIP_TONES = {
  green: "bg-[#E7F6EE] text-[#067647]",
  blue: "bg-[#EAF1FF] text-[#2563EB]",
  slate: "bg-slate-100 text-nova-slate",
  red: "bg-red-50 text-red-600",
};

const RowIcon = ({ tone }) => {
  if (tone === "check-green")
    return (
      <span className="w-5 h-5 rounded-full bg-[#E7F6EE] flex items-center justify-center shrink-0">
        <Check size={11} strokeWidth={3} className="text-[#067647]" />
      </span>
    );
  if (tone === "check-blue")
    return (
      <span className="w-5 h-5 rounded-full bg-nova-bluelight/70 flex items-center justify-center shrink-0">
        <Check size={11} strokeWidth={3} className="text-nova-blue" />
      </span>
    );
  if (tone === "alert")
    return (
      <span className="w-5 h-5 rounded-full bg-red-50 flex items-center justify-center shrink-0">
        <PhoneCall size={11} className="text-red-500" />
      </span>
    );
  if (tone === "user")
    return (
      <span className="w-5 h-5 rounded-full bg-nova-mist flex items-center justify-center shrink-0">
        <UserRound size={11} className="text-nova-blue" />
      </span>
    );
  return <span className="w-1.5 h-1.5 rounded-full bg-nova-blue shrink-0 mt-1.5" />;
};

const ListVisual = ({ v }) => (
  <div className="flex flex-col gap-2.5 min-w-0">
    {v.header && (
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-sm font-bold text-nova-navy leading-tight min-w-0 [overflow-wrap:anywhere]">
          {v.header.title}
        </span>
        {v.header.pill && (
          <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold whitespace-nowrap shrink-0 ${CHIP_TONES[v.header.pill.tone]}`}>
            {v.header.pill.text}
          </span>
        )}
        {v.header.icon === "send" && (
          <span className="w-7 h-7 rounded-full bg-nova-blue flex items-center justify-center shrink-0">
            <Send size={12} className="text-white" />
          </span>
        )}
        {v.header.icon === "file" && (
          <span className="w-7 h-7 rounded-full bg-nova-mist flex items-center justify-center shrink-0">
            <FileText size={13} className="text-nova-blue" />
          </span>
        )}
      </div>
    )}
    {v.rows.map((r) => (
      <div key={r.title} className={`flex flex-wrap min-w-0 ${r.stacked ? "flex-col items-start gap-1.5" : "items-center gap-x-2.5 gap-y-1"}`}>
        <RowIcon tone={r.icon} />
        <div className="min-w-0 flex-1 basis-24">
          <p className={`text-xs xl:text-[13px] 2xl:text-xs font-semibold leading-tight [overflow-wrap:anywhere] ${r.tone === "red" ? "text-red-600" : "text-nova-navy"}`}>
            {r.title}
          </p>
          {r.sub && <p className="text-[10px] xl:text-[11px] 2xl:text-[10px] text-nova-muted mt-0.5 [overflow-wrap:anywhere]">{r.sub}</p>}
        </div>
        {r.chip && (
          <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold whitespace-nowrap shrink-0 ${CHIP_TONES[r.chip.tone]}`}>
            {r.chip.text}
          </span>
        )}
      </div>
    ))}
    {v.footer && (
      <p className={`text-[11px] font-semibold pt-1 border-t border-slate-100 ${v.footer.tone === "green" ? "text-[#067647]" : "text-nova-muted"}`}>
        {v.footer.text}
      </p>
    )}
  </div>
);

const InvoiceVisual = ({ v }) => (
  <div className="flex flex-col gap-3 min-w-0">
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <span className="text-sm font-bold text-nova-navy [overflow-wrap:anywhere]">{v.number}</span>
      <span className="rounded-md bg-red-50 px-2 py-0.5 text-[10px] font-bold tracking-wide text-red-600 whitespace-nowrap shrink-0">
        {v.badge}
      </span>
    </div>
    <div className="space-y-1.5">
      <div className="h-1.5 w-4/5 rounded-full bg-slate-100" />
      <div className="h-1.5 w-3/5 rounded-full bg-slate-100" />
    </div>
    <div>
      <p className="text-2xl font-extrabold tracking-tight text-nova-navy">{v.amount}</p>
      <p className="text-xs font-semibold text-red-500 mt-0.5">{v.due}</p>
    </div>
  </div>
);

const KvVisual = ({ v }) => (
  <div className="flex flex-col gap-2 min-w-0">
    {v.rows.map((r) => (
      <div key={r.label} className="flex items-center justify-between gap-2 min-w-0">
        <span className="text-xs xl:text-[13px] 2xl:text-xs text-nova-muted min-w-0 [overflow-wrap:anywhere]">{r.label}</span>
        {r.tone ? (
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold whitespace-nowrap shrink-0 ${CHIP_TONES[r.tone]}`}>{r.value}</span>
        ) : (
          <span className="text-xs xl:text-[13px] 2xl:text-xs font-semibold text-nova-navy text-right min-w-0 [overflow-wrap:anywhere]">{r.value}</span>
        )}
      </div>
    ))}
  </div>
);

const EmailVisual = ({ v }) => (
  <div className="min-w-0">
    <div className="flex items-start justify-between gap-2">
      <p className="text-sm font-bold text-nova-navy leading-tight min-w-0 [overflow-wrap:anywhere]">{v.title}</p>
      <span className="w-8 h-8 rounded-full bg-nova-blue flex items-center justify-center shrink-0 shadow-[0_6px_16px_rgb(37,99,235,0.35)]">
        <Send size={13} className="text-white" />
      </span>
    </div>
    <p className="mt-2 text-xs xl:text-[13px] 2xl:text-xs text-nova-slate leading-relaxed [overflow-wrap:anywhere]">{v.body}</p>
  </div>
);

const DocVisual = ({ v }) => (
  <div className="flex flex-col items-start gap-2.5 min-w-0">
    <div className="relative">
      <span className="w-12 h-12 rounded-2xl bg-nova-mist border border-nova-bluelight flex items-center justify-center">
        <FileText size={20} className="text-nova-blue" />
      </span>
      <span className="absolute -bottom-1.5 -right-1.5 w-5 h-5 rounded-full bg-nova-blue flex items-center justify-center">
        <Upload size={10} className="text-white" />
      </span>
    </div>
    <div className="min-w-0">
      <p className="text-sm font-bold text-nova-navy [overflow-wrap:anywhere]">{v.name}</p>
      <p className="text-[10px] xl:text-[11px] text-nova-muted mt-0.5 [overflow-wrap:anywhere]">{v.from}</p>
    </div>
  </div>
);

const FolderVisual = ({ v }) => (
  <div className="flex flex-col gap-2 min-w-0">
    {v.rows.map((r) => (
      <div key={r.title} className="flex items-center gap-2 min-w-0" style={{ paddingLeft: r.depth * 12 }}>
        {r.file ? (
          <FileText size={14} className="text-red-400 shrink-0" />
        ) : (
          <Folder size={14} className="text-nova-blue shrink-0" />
        )}
        <span className={`text-xs xl:text-[13px] 2xl:text-xs min-w-0 [overflow-wrap:anywhere] ${r.file ? "font-semibold text-nova-navy" : "font-medium text-nova-slate"}`}>
          {r.title}
        </span>
      </div>
    ))}
  </div>
);

const BigVisual = ({ v }) => (
  <div className="h-full min-h-[120px] flex flex-col items-center justify-center text-center gap-3">
    <span className="w-12 h-12 rounded-full bg-[#067647] flex items-center justify-center shadow-[0_8px_20px_rgb(6,118,71,0.3)] shrink-0">
      <Check size={22} strokeWidth={3} className="text-white" />
    </span>
    <p className="text-sm font-bold text-nova-navy leading-snug [overflow-wrap:anywhere]">{v.title}</p>
  </div>
);

const StageVisual = ({ v }) => {
  switch (v.kind) {
    case "invoice": return <InvoiceVisual v={v} />;
    case "kv": return <KvVisual v={v} />;
    case "email": return <EmailVisual v={v} />;
    case "doc": return <DocVisual v={v} />;
    case "folder": return <FolderVisual v={v} />;
    case "big": return <BigVisual v={v} />;
    default: return <ListVisual v={v} />;
  }
};

const StageCard = ({ s, i }) => (
  <div className="min-w-0 flex flex-col" data-testid={`workflow-stage-${i + 1}`}>
    <div className="flex-1 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-soft min-h-[128px]">
      <StageVisual v={s.visual} />
    </div>
    <p className="mt-3 text-sm font-bold text-nova-navy [overflow-wrap:anywhere]">{s.title}</p>
    <p className="mt-1 text-xs xl:text-[13px] text-nova-slate leading-relaxed [overflow-wrap:anywhere]">{s.desc}</p>
  </div>
);

export const WorkflowPanel = ({ wf }) => (
  <div data-testid="workflow-panel" className="rounded-[22px] border border-slate-200/70 bg-white p-6 lg:p-8 shadow-[0_10px_40px_rgb(10,25,47,0.05)]">
    <div className="flex items-center gap-4">
      <span className="w-11 h-11 rounded-xl bg-[#2563FF] text-white text-sm font-extrabold flex items-center justify-center shrink-0 shadow-[0_8px_20px_rgb(37,99,255,0.35)]">
        {wf.num}
      </span>
      <div className="min-w-0">
        <h3 data-testid="workflow-title" className="text-xl lg:text-2xl font-bold tracking-tight text-nova-navy [overflow-wrap:anywhere]">
          {wf.name}
        </h3>
        <p className="text-sm text-nova-slate mt-0.5 [overflow-wrap:anywhere]">{wf.subtitle}</p>
      </div>
    </div>

    {/* Up to xl: adaptive grid (1 / 2 / 3 columns, rows stretch to tallest card) */}
    <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 2xl:hidden">
      {wf.stages.map((s, i) => (
        <StageCard key={s.title} s={s} i={i} />
      ))}
    </div>

    {/* Wide desktop: one horizontal row with flow arrows */}
    <div className="mt-7 hidden 2xl:flex 2xl:items-stretch gap-1">
      {wf.stages.map((s, i) => (
        <Fragment key={s.title}>
          {i > 0 && (
            <MoveRight size={16} strokeWidth={2} className="self-center text-nova-blue shrink-0 mx-1" aria-hidden="true" />
          )}
          <div className="flex-1 min-w-0 flex">
            <StageCard s={s} i={i} />
          </div>
        </Fragment>
      ))}
    </div>

    <div data-testid="workflow-metrics" className="mt-8 rounded-2xl bg-[#F5F8FE] border border-nova-bluelight/40 px-5 py-5 grid grid-cols-2 xl:grid-cols-4 gap-5">
      {wf.metrics.map((m) => (
        <div key={m.label} className="flex items-center gap-3 min-w-0">
          <span className="w-10 h-10 rounded-full bg-white border border-nova-bluelight flex items-center justify-center shrink-0">
            <m.icon size={16} strokeWidth={1.8} className="text-nova-blue" />
          </span>
          <div className="min-w-0">
            <p className="text-lg font-extrabold tracking-tight text-nova-navy leading-none">{m.value}</p>
            <p className="text-[11px] text-nova-slate mt-1 leading-tight [overflow-wrap:anywhere]">{m.label}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);
